import { useMemo } from "react";
import type { EstimateDetail, LineItem, Tuple } from "@/interfaces/estimate-detail";
import { isItemArchived } from "@rebuild/shared";

type SimilarRoomMatch = {
  pageId: string;
  roomName: string;
  items: Array<{
    tuple: Tuple;
    lineItem: LineItem;
  }>;
};

function isIssue(tuple: Tuple, li: LineItem): boolean {
  return !tuple.isComplementOf && tuple.hasError && !tuple.isFreeFormFlag && !li.isComplement && !li.isMutuallyExcluded;
}
export function useSimilarIssuesIndex(rooms: EstimateDetail[]) {
  return useMemo(() => {
    // key -> pageId -> items[]
    const index = new Map<string, Map<string, SimilarRoomMatch>>();

    for (const room of rooms) {
      const pageId = room.dataForUI.metadata.pageId;
      const roomName = room.dataForUI.metadata.roomName;
      const tuples = room.dataForUI.page.tuplesOutput || [];

      for (const tuple of tuples) {
        if (!tuple) continue;

        for (const li of tuple.lineItems || []) {
          if (!isIssue(tuple, li) || isItemArchived(li) || isItemArchived(tuple)) continue;

          const key = li.lineItemId;

          if (!index.has(key)) index.set(key, new Map());
          const perRoom = index.get(key)!;
          if (!perRoom.has(pageId)) perRoom.set(pageId, { pageId, roomName, items: [] });
          perRoom.get(pageId)!.items.push({
            tuple,
            lineItem: li,
          });
        }
      }
    }

    return {
      getSimilarRooms(key: string, excludePageId: string): SimilarRoomMatch[] {
        const perRoom = index.get(key);
        if (!perRoom) return [];
        return [...perRoom.entries()].filter(([pid]) => pid !== excludePageId).map(([pid, v]) => ({ ...v, pageId: pid }));
      },
      keyFromItem(li: LineItem) {
        return li.lineItemId;
      },
    };
  }, [rooms]);
}
